import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import posthog from "posthog-js";
import { onGetUsers, onSearchUsers, onGetOffices, onGetDepartments, onDownloadBulkUploadTemplate, onBulkUploadUsers, onDeleteUser } from "./userActions";
import { useSubscriptionStatus } from "../../../hooks/useSubscriptionStatus";
import SubscriptionRestriction from "../../common/subscriptionRestriction/SubscriptionRestriction";
import { GridPager } from "../../common/pagination/GridPager";
import UsersDataGrid, { usersActionsCellClass, usersActionsHeaderClass } from "./UsersDataGrid";
import {
  buildUsersExportQuery,
  fetchUsersExportCsv,
  fetchUsersExportExcel,
  fetchUsersExportPdf,
  triggerBlobDownload,
  usersExportBasename,
} from "./usersExportApi";
import {
  Button,
  Modal,
  Icon,
  Popup,
  Loader,
} from "semantic-ui-react";
import * as constant from "../../Global/Constant";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { WebStorage, WebStorageNames, Common } from "../../../utils";
import { useWorkplaceGate } from "../../../hooks/useWorkplaceGate";

import "rc-pagination/assets/index.css";
import './users.css';
import './userSearch.css';
import './usersGrid.css';
import { setProfileViewUserId } from "./profileViewStorage";

const USER_SORT = {
  name: "name",
  email: "email",
  workplace: "workplace",
  department: "department",
  role: "role",
  startDate: "startDate",
  totalLeaves: "totalLeaves",
};


// Add a color palette for avatars
const avatarColors = [
  '#007bff', '#00b894', '#fdcb6e', '#e17055', '#6c5ce7', '#00b8d9', '#fd79a8', '#636e72', '#e67e22', '#0984e3'
];

// Hash function to pick a color based on user id or name
function getAvatarColor(user) {
  const str = user.id || user.email || user.name || '';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const idx = Math.abs(hash) % avatarColors.length;
  return avatarColors[idx];
}

const Users = (props) => {
  const { t } = useTranslation();
  const [currentMonth, setCurrentMonth] = useState("");
  const [currentYear, setCurrentYear] = useState("");
  const [pageLimit, setPageLimit] = useState(10);
  const [isActive, setActive] = useState(true);
  const [selectedOfficeId, setSelectedOfficeId] = useState("all");
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("all");
  const [lastSelectedValue, setLastSelectedValue] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [sortState, setSortState] = useState({ key: USER_SORT.name, dir: "asc" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [exportLoading, setExportLoading] = useState(null);

  const activeStatusOptions = [
    { key: "Active", text: t("app.users.active"), value: "true" },
    { key: "Inactive", text: t("app.users.inactive"), value: "false" },
  ];

  const dispatch = useDispatch();
  const { withWorkplace, workplaceRequiredModal } = useWorkplaceGate("users");
  const userStore = useSelector((state) => state.userReducer);
  const pagingStore = useSelector((state) => state.pagingReducer);
  const usersPaginationRangeLabel = useMemo(() => {
    const currentPage = pagingStore.currentPage ?? 1;
    const limit = pagingStore.pageLimit ?? 10;
    const total = pagingStore.totalItems ?? 0;
    if (total === 0) return "No results";
    const from = (currentPage - 1) * limit + 1;
    const to = Math.min(currentPage * limit, total);
    return `Showing ${from}–${to} of ${total}`;
  }, [pagingStore.currentPage, pagingStore.pageLimit, pagingStore.totalItems]);
  const { isSuspended } = useSubscriptionStatus();

  // Get current user's roleId to check if admin
  const getCurrentUserRoleId = () => {
    try {
      return WebStorage.getLocalStore(WebStorageNames.RoleId);
    } catch (e) {
      return localStorage.getItem("roleId");
    }
  };

  const currentUserRoleId = getCurrentUserRoleId();
  const isCurrentUserAdmin = currentUserRoleId && (parseInt(currentUserRoleId) === 1 || parseInt(currentUserRoleId) === 2);

  const loading = userStore.usersLoading;
  const initialLoadDoneRef = useRef(false);
  const searchEffectInitialMountRef = useRef(true);

  useEffect(() => {
    const date = new Date();
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    setCurrentMonth(monthNames[date.getMonth()]);
    setCurrentYear(date.getFullYear());

    // Avoid double fetch when useEffect runs twice (e.g. React Strict Mode)
    if (initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;

    // Only load users on initial mount if there's no search text
    if (!searchText || searchText.trim() === '') {
      const params = {
        page: 1,
        start: 0,
        limit: pagingStore.pageLimit,
        isActive: isActive,
        officeId: selectedOfficeId === "all" ? 0 : selectedOfficeId,
        departmentId: selectedDepartmentId === "all" ? 0 : selectedDepartmentId,
        searchText: "",
      };
      const currentContext = {
        limit: params.limit ?? pagingStore.pageLimit ?? 10,
        isActive: params.isActive,
        officeId: Number(params.officeId) || 0,
        departmentId: Number(params.departmentId) || 0,
      };
      const ctx = userStore.usersListContext;
      const hasDataForThisContext =
        userStore.users &&
        Array.isArray(userStore.users) &&
        ctx &&
        ctx.limit === currentContext.limit &&
        ctx.isActive === currentContext.isActive &&
        Number(ctx.officeId) === currentContext.officeId &&
        Number(ctx.departmentId) === currentContext.departmentId;

      if (!hasDataForThisContext && !userStore.usersLoading) {
        dispatch(onGetUsers(params, 1));
      }
    }

    dispatch(onGetOffices(true));
    dispatch(onGetDepartments(true));
  }, []);

  useEffect(() => {
    if (searchText === undefined) return;

    const searchTimeout = setTimeout(() => {
      if (searchText && searchText.trim() !== '') {
        setIsSearching(true);
        handleSearch(searchText);
      } else if (searchText === '') {
        // Only reset to all users when user explicitly cleared search, not on initial mount
        if (searchEffectInitialMountRef.current) {
          searchEffectInitialMountRef.current = false;
        } else {
          setIsSearching(false);
          const params = {
            page: 1,
            start: 0,
            limit: pageLimit,
            isActive: isActive,
            officeId: selectedOfficeId === "all" ? 0 : selectedOfficeId,
            departmentId: selectedDepartmentId === "all" ? 0 : selectedDepartmentId,
            searchText: "",
          };
          dispatch(onGetUsers(params, 1));
        }
      }
    }, 500);

    return () => clearTimeout(searchTimeout);
  }, [searchText]);

  const handleSearch = (text) => {
    if (!text || text.trim() === '') {
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const params = {
      page: 1,
      start: 0,
      limit: pageLimit,
      isActive: isActive,
      officeId: selectedOfficeId === "all" ? 0 : selectedOfficeId,
      departmentId: selectedDepartmentId === "all" ? 0 : selectedDepartmentId,
      searchText: text.trim(),
    };
    // onSearchUsers will handle loading state internally
    // Note: Keep isSearching true as long as we have search text
    // This prevents the useEffect from resetting to all users after search completes
    dispatch(onSearchUsers(params, 1));
  };

  const pagingOnChange = (current, pageSize) => {
    // Use the pageSize from the parameter, but fallback to store if needed
    const actualPageSize = pageSize || pagingStore.pageLimit || pageLimit;

    // Calculate start parameter (0-based index)
    // Page 1: start = 0, Page 2: start = pageSize, Page 3: start = 2*pageSize, etc.
    const start = (current - 1) * actualPageSize;

    let params = {
      officeId: selectedOfficeId === "all" ? 0 : selectedOfficeId,
      departmentId: selectedDepartmentId === "all" ? 0 : selectedDepartmentId,
      page: current,
      start: start,
      limit: actualPageSize,
      isActive: isActive,
      searchText: searchText,
    };

    // If there's search text, use search endpoint, otherwise use regular get users
    if (searchText && searchText.trim() !== '') {
      dispatch(onSearchUsers(params, current));
    } else {
      dispatch(onGetUsers(params, current));
    }
  };

  const handleActiveStatusChange = (e, { value }) => {
    setActive(value === "true");
    const params = {
      officeId: selectedOfficeId === "all" ? 0 : selectedOfficeId,
      departmentId: selectedDepartmentId === "all" ? 0 : selectedDepartmentId,
      page: 1,
      start: 0,
      limit: pageLimit,
      isActive: value === "true",
      searchText: searchText,
    };
    // onGetUsers will handle loading state internally
    dispatch(onGetUsers(params, 1));
  };

  const handlePageLimitChange = (e, { value }) => {
    if (lastSelectedValue !== value) {
      setPageLimit(value);
      setLastSelectedValue(value);
      const params = {
        officeId: selectedOfficeId === "all" ? 0 : selectedOfficeId,
        departmentId: selectedDepartmentId === "all" ? 0 : selectedDepartmentId,
        page: 1,
        start: 0,
        limit: value,
        isActive: isActive,
        searchText: searchText,
      };
      // onGetUsers will handle loading state internally
      dispatch(onGetUsers(params, 1));
    }
  };

  const handleOfficeChange = (e, { value }) => {
    setSelectedOfficeId(value);
    const params = {
      officeId: value === "all" ? 0 : value,
      departmentId: selectedDepartmentId === "all" ? 0 : selectedDepartmentId,
      page: 1,
      start: 0,
      limit: pageLimit,
      isActive: isActive,
      searchText: searchText,
    };
    // onGetUsers will handle loading state internally
    dispatch(onGetUsers(params, 1));
  };

  const handleDepartmentChange = (e, { value }) => {
    setSelectedDepartmentId(value);
    const params = {
      officeId: selectedOfficeId === "all" ? 0 : selectedOfficeId,
      departmentId: value === "all" ? 0 : value,
      page: 1,
      start: 0,
      limit: pageLimit,
      isActive: isActive,
      searchText: searchText,
    };
    // onGetUsers will handle loading state internally
    dispatch(onGetUsers(params, 1));
  };

  const addNewUser = () => {
    withWorkplace(() => props.history.push("/dashboard/user-form"));
  };

  const editUser = (userId) => {
    props.history.push({
      pathname: "/dashboard/user-form",
      state: { userId, mode: "edit" },
    });
  };

  const handleDeleteClick = (userId, userName) => {
    setUserToDelete({ id: userId, name: userName });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    const params = {
      officeId: selectedOfficeId === "all" ? 0 : selectedOfficeId,
      departmentId: selectedDepartmentId === "all" ? 0 : selectedDepartmentId,
      page: pagingStore.currentPage,
      start: (pagingStore.currentPage - 1) * pagingStore.pageLimit,
      limit: pagingStore.pageLimit,
      isActive: isActive,
      searchText: searchText,
    };

    const result = await dispatch(onDeleteUser(userToDelete.id, params));
    if (result?.success) {
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true);
    try {
      await dispatch(onDownloadBulkUploadTemplate());
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        alert('Please select a valid Excel file (.xlsx or .xls)');
        e.target.value = null;
        return;
      }

      setUploadFile(file);
      setUploadErrors([]);
    }
    e.target.value = null;
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      alert('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    setUploadErrors([]);

    const params = {
      officeId: selectedOfficeId === "all" ? 0 : selectedOfficeId,
      departmentId: selectedDepartmentId === "all" ? 0 : selectedDepartmentId,
      page: pagingStore.currentPage || 1,
      start: ((pagingStore.currentPage || 1) - 1) * pagingStore.pageLimit,
      limit: pagingStore.pageLimit,
      isActive: isActive,
      searchText: searchText,
    };

    try {
      const result = await dispatch(onBulkUploadUsers(uploadFile, params));
      if (result.success) {
        setUploadFile(null);
        setShowBulkUploadModal(false);
      } else {
        if (result.errors && result.errors.length > 0) {
          setUploadErrors(result.errors);
        }
      }
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("usersBulkUpload", {
          type: "exception",
          success: false,
          reason: error?.response?.data?.message ?? error?.message ?? "Upload failed",
          status: error?.response?.status,
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleCloseBulkUploadModal = () => {
    setShowBulkUploadModal(false);
    setUploadFile(null);
    setUploadErrors([]);
  };

  const officeOptions = [
    { key: "all", text: "All", value: "all" },
    ...(userStore.offices
      ? userStore.offices.map((office) => ({
        key: office.key,
        text: office.text,
        value: office.value,
      }))
      : []),
  ];

  const departmentOptions = [
    { key: "all", text: "All", value: "all" },
    ...(userStore.departments
      ? userStore.departments.map((department) => ({
        key: department.key,
        text: department.text,
        value: department.value,
      }))
      : []),
  ];

  const displayRecordOptions = constant.DisplayRecordoptions
    ? constant.DisplayRecordoptions.map((option) => ({
      key: option.value,
      text: option.text,
      value: option.value,
    }))
    : [];

  const handleSort = (columnKey) => {
    setSortState((prev) =>
      prev.key === columnKey
        ? { key: columnKey, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key: columnKey, dir: "asc" }
    );
  };

  const clearFilters = () => {
    setSearchText("");
    setIsSearching(false);
    setSelectedOfficeId("all");
    setSelectedDepartmentId("all");
    setPageLimit(10);
    setLastSelectedValue(10);
    dispatch(
      onGetUsers(
        {
          page: 1,
          start: 0,
          limit: 10,
          isActive: isActive,
          officeId: 0,
          departmentId: 0,
          searchText: "",
        },
        1
      )
    );
  };

  const hasActiveFilters =
    (searchText && searchText.trim() !== "") ||
    selectedOfficeId !== "all" ||
    selectedDepartmentId !== "all";

  const usersExportQueryArgs = useMemo(
    () =>
      buildUsersExportQuery({
        officeId: selectedOfficeId === "all" ? 0 : selectedOfficeId,
        departmentId: selectedDepartmentId === "all" ? 0 : selectedDepartmentId,
        isActive: isActive,
        search: searchText,
      }),
    [selectedOfficeId, selectedDepartmentId, isActive, searchText]
  );

  const runExportExcel = async () => {
    setExportLoading("excel");
    try {
      const blob = await fetchUsersExportExcel(usersExportQueryArgs);
      triggerBlobDownload(blob, `${usersExportBasename()}.xlsx`);
    } catch (e) {
      Common.showErrorMessage(e?.response?.data?.message || e?.message || t("app.users.exportFailed"));
    } finally {
      setExportLoading(null);
    }
  };

  const runExportCsv = async () => {
    setExportLoading("csv");
    try {
      const blob = await fetchUsersExportCsv(usersExportQueryArgs);
      triggerBlobDownload(blob, `${usersExportBasename()}.csv`);
    } catch (e) {
      Common.showErrorMessage(e?.response?.data?.message || e?.message || t("app.users.exportFailed"));
    } finally {
      setExportLoading(null);
    }
  };

  const runExportPdf = async () => {
    setExportLoading("pdf");
    try {
      const blob = await fetchUsersExportPdf(usersExportQueryArgs);
      triggerBlobDownload(blob, `${usersExportBasename()}.pdf`);
    } catch (e) {
      Common.showErrorMessage(e?.response?.data?.message || e?.message || t("app.users.exportFailed"));
    } finally {
      setExportLoading(null);
    }
  };

  const sortUsers = (users) => {
    const { key: sortColumn, dir: sortDirection } = sortState;
    if (!users || !Array.isArray(users)) {
      return [];
    }
    if (!sortColumn) {
      return users;
    }

    const sorted = [...users].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case USER_SORT.name:
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case USER_SORT.email:
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case USER_SORT.workplace:
          aValue = (a.offices?.name || '').toLowerCase();
          bValue = (b.offices?.name || '').toLowerCase();
          break;
        case USER_SORT.department:
          aValue = (a.department || '').toLowerCase();
          bValue = (b.department || '').toLowerCase();
          break;
        case USER_SORT.role:
          aValue = (a.roleName || '').toLowerCase();
          bValue = (b.roleName || '').toLowerCase();
          break;
        case USER_SORT.startDate:
          aValue = a.employementStartDate ? new Date(a.employementStartDate).getTime() : 0;
          bValue = b.employementStartDate ? new Date(b.employementStartDate).getTime() : 0;
          break;
        case USER_SORT.totalLeaves:
          aValue = a.totalLeaveBalance || 0;
          bValue = b.totalLeaveBalance || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  };

  const usersListRows = useMemo(
    () => (Array.isArray(userStore.users) ? userStore.users : []),
    [userStore.users]
  );

  const sortedUsers = useMemo(
    () => sortUsers(usersListRows),
    [usersListRows, sortState]
  );

  const columns = useMemo(
    () => [
      { key: "photo", label: t("app.users.tableHeaderPhoto"), sortable: false, headerClassName: "users-grid-th-photo" },
      { key: USER_SORT.name, label: t("app.users.tableHeaderName"), sortable: true },
      { key: USER_SORT.email, label: t("app.users.tableHeaderEmail"), sortable: true },
      { key: USER_SORT.workplace, label: t("app.users.tableHeaderWorkplace"), sortable: true },
      { key: USER_SORT.department, label: t("app.users.tableHeaderDepartment"), sortable: true },
      { key: USER_SORT.role, label: t("app.users.tableHeaderJobRole"), sortable: true },
      { key: USER_SORT.startDate, label: t("app.users.tableHeaderStartDate"), sortable: true },
      { key: USER_SORT.totalLeaves, label: t("app.users.tableHeaderLeaveStatistics"), sortable: true },
      { key: "actions", label: t("app.users.tableHeaderActions"), sortable: false, headerClassName: usersActionsHeaderClass },
    ],
    [t]
  );

  const openUserProfile = (user) => {
    if (!user?.id) return;
    setProfileViewUserId(user.id);
    props.history.push({
      pathname: "/users/view",
      state: { userId: user.id, from: "/users" },
    });
  };

  const renderUserRow = (user) => (
    <>
      <td className="users-grid-photo-cell">
        {user?.imageURL ? (
          <Popup
            trigger={
              <img
                src={user.imageURL}
                alt={user.name}
                className="users-table-avatar rounded-circle"
              />
            }
            content={
              <img
                src={user.imageURL}
                alt={user.name}
                style={{ width: 120, height: 120, objectFit: "cover", borderRadius: "50%", display: "block" }}
              />
            }
            on="hover"
            hoverable
            position="top left"
          />
        ) : (
          <div
            className="users-table-avatar-initial rounded-circle text-white d-flex align-items-center justify-content-center"
            style={{ background: getAvatarColor(user) }}
          >
            {user.name?.charAt(0).toUpperCase()}
          </div>
        )}
      </td>
      <td>
        <span className="users-table-name fw-semibold" title={user.name}>
          {user.name && user.name.length > 28 ? `${user.name.substring(0, 28)}...` : user.name}
        </span>
      </td>
      <td>
        <span className="text-truncate d-inline-block users-grid-email" title={user.email}>
          {user.email}
        </span>
      </td>
      <td className="text-muted">{user.offices?.name || "—"}</td>
      <td className="text-muted">{user.department || "—"}</td>
      <td className="text-muted">{user.role ? user.roleName : "—"}</td>
      <td className="text-nowrap">
        {user.employementStartDate && user.employementStartDate !== "0001-01-01T00:00:00"
          ? new Date(user.employementStartDate).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "—"}
      </td>
      <td className="users-leave-stats">
        <div className="leave-stats-container">
          <div className="leave-stat-item">
            <span className="leave-stat-label">Total:</span>
            <span className="leave-stat-value">{user.totalLeaveBalance || 0}</span>
          </div>
          <div className="leave-stat-item">
            <span className="leave-stat-label">Remaining:</span>
            <span className="leave-stat-value remaining">{user.totalRemainingLeaves || 0}</span>
          </div>
          <div className="leave-stat-item">
            <span className="leave-stat-label">Used:</span>
            <span className="leave-stat-value used">{user.totalUsedLeaves || 0}</span>
          </div>
        </div>
      </td>
      <td className={usersActionsCellClass}>
        <div className="users-grid-actions-inline d-flex flex-wrap gap-1 justify-content-center">
          <button
            type="button"
            className="users-grid-action-btn users-grid-action-btn--view users-grid-action-btn--compact"
            onClick={() => openUserProfile(user)}
          >
            View
          </button>
          <button
            type="button"
            className="users-grid-action-btn users-grid-action-btn--edit users-grid-action-btn--compact"
            onClick={() => editUser(user.id)}
          >
            Edit
          </button>
          {(!user.role || (user.role !== 1 && user.role !== 2)) && (
            <button
              type="button"
              className="users-grid-action-btn users-grid-action-btn--delete users-grid-action-btn--compact"
              onClick={() => handleDeleteClick(user.id, user.name)}
            >
              Delete
            </button>
          )}
        </div>
      </td>
    </>
  );

  // If subscription is suspended, show restriction message instead of users list
  if (isSuspended) {
    return (
      <div style={{ padding: "20px" }}>
        <SubscriptionRestriction actionKey="app.subscriptionRestriction.actionViewUsers" />
      </div>
    );
  }

  return (
    <div className="users-page container-fluid px-3 px-md-4 py-4">
      {/* Header Section */}
      <CommonPageHeader
        barClassName="users-header-bar"
        contentClassName="users-header-content"
        leftClassName="users-header-left"
        iconWrapperClassName="users-header-icon-wrapper"
        textClassName="users-header-text"
        titleClassName="users-page-title"
        descriptionClassName="users-page-description"
        rightClassName="users-header-right"
        icon={<i className="bi bi-people-fill" />}
        title={t("app.users.usersTitle")}
        description={t("app.users.usersDescription")}
        rightContent={
          <>
            <Button
              className="users-bulk-upload-button"
              onClick={() => setShowBulkUploadModal(true)}
              style={{ marginRight: '10px' }}
            >
              <Icon name="upload" />
              <span>{t("app.users.bulkUpload")}</span>
            </Button>
            <Button
              className={`users-add-button${loading ? " is-loading" : ""}`}
              onClick={addNewUser}
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? (
                <span
                  className="spinner-border spinner-border-sm users-add-button__spinner"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <Icon name="user plus" />
              )}
              <span>{t("app.users.addNewUserEmployee")}</span>
            </Button>
          </>
        }
      />

      <section className="users-filter-shell" aria-label={t("app.timesheets.approvals.filtersAria")}>
        <div className="users-filters" role="search">
          <div className="users-filters__header">
            <div className="users-filters__title" id="users-filters-heading">
              {t("app.timesheets.approvals.filtersToolbarLabel")}
            </div>
            <div
              className="users-export-group"
              role="group"
              aria-label={t("app.users.exportActionsAria")}
            >
              <button
                type="button"
                className="users-export-btn users-export-btn--excel users-export-btn--labeled"
                onClick={() => runExportExcel()}
                disabled={Boolean(exportLoading) || loading}
                title={t("app.users.exportExcelTitle")}
              >
                {exportLoading === "excel" ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                ) : (
                  <i className="bi bi-file-earmark-spreadsheet" aria-hidden />
                )}
                <span className="users-export-btn__label">{t("app.timesheets.common.exportExcelShort")}</span>
              </button>
              <button
                type="button"
                className="users-export-btn users-export-btn--csv users-export-btn--labeled"
                onClick={() => runExportCsv()}
                disabled={Boolean(exportLoading) || loading}
                title={t("app.users.exportCsvTitle")}
              >
                {exportLoading === "csv" ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                ) : (
                  <i className="bi bi-filetype-csv" aria-hidden />
                )}
                <span className="users-export-btn__label">{t("app.timesheets.common.exportCsvShort")}</span>
              </button>
              <button
                type="button"
                className="users-export-btn users-export-btn--pdf users-export-btn--labeled"
                onClick={() => runExportPdf()}
                disabled={Boolean(exportLoading) || loading}
                title={t("app.users.exportPdfTitle")}
              >
                {exportLoading === "pdf" ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                ) : (
                  <i className="bi bi-file-earmark-pdf" aria-hidden />
                )}
                <span className="users-export-btn__label">{t("app.timesheets.common.exportPdfShort")}</span>
              </button>
            </div>
          </div>
          <div className="users-filters__fields">
            <div className="users-filter-field">
              <label className="users-filter-label" htmlFor="users-search">
                {t("app.timesheets.approvals.searchEmployee")}
              </label>
              <input
                id="users-search"
                type="search"
                autoComplete="off"
                placeholder={t("app.users.searchUsersPlaceholder")}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="users-filter-field">
              <label className="users-filter-label" htmlFor="users-office">
                {t("app.timesheets.approvals.filterOffice")}
              </label>
              <select
                id="users-office"
                value={selectedOfficeId}
                onChange={(e) => handleOfficeChange(e, { value: e.target.value })}
                disabled={loading}
              >
                {officeOptions.map((opt) => (
                  <option key={opt.key} value={opt.value}>
                    {opt.text}
                  </option>
                ))}
              </select>
            </div>
            <div className="users-filter-field">
              <label className="users-filter-label" htmlFor="users-department">
                {t("app.timesheets.approvals.filterDepartment")}
              </label>
              <select
                id="users-department"
                value={selectedDepartmentId}
                onChange={(e) => handleDepartmentChange(e, { value: e.target.value })}
                disabled={loading}
              >
                {departmentOptions.map((opt) => (
                  <option key={opt.key} value={opt.value}>
                    {opt.text}
                  </option>
                ))}
              </select>
            </div>
            <div className="users-filter-field">
              <label className="users-filter-label" htmlFor="users-page-size">
                {t("app.attendance.reports.rowsPerPage")}
              </label>
              <select
                id="users-page-size"
                value={pageLimit}
                onChange={(e) => handlePageLimitChange(e, { value: Number(e.target.value) })}
                disabled={loading}
              >
                {displayRecordOptions.map((opt) => (
                  <option key={opt.key} value={opt.value}>
                    {opt.text}
                  </option>
                ))}
              </select>
            </div>
            <div className="users-filter-field users-filters__clear-field">
              <label className="users-filter-label" htmlFor="users-clear-filters">
                {t("app.timesheets.approvals.clearFiltersLabel")}
              </label>
              <button
                id="users-clear-filters"
                type="button"
                className="btn btn-outline-secondary users-filters__clear-btn"
                onClick={clearFilters}
                disabled={loading || !hasActiveFilters}
              >
                <i className="bi bi-x-lg me-1" aria-hidden />
                {t("app.timesheets.approvals.clearFilters")}
              </button>
            </div>
          </div>
        </div>
      </section>

      {hasActiveFilters ? (
        <p className="users-results-hint mb-3">{usersPaginationRangeLabel}</p>
      ) : null}

      <div className="users-grid-section">
        <UsersDataGrid
          columns={columns}
          rows={sortedUsers}
          getRowKey={(user) => String(user.id)}
          renderRow={renderUserRow}
          sortKey={sortState.key}
          sortDir={sortState.dir}
          onSort={handleSort}
          loading={loading}
          loadingMessage={t("app.users.loadingUsers")}
          emptyMessage={t("app.users.noUsersFound")}
        />

        <div className="users-pagination d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2">
          <span className="users-pagination__total text-muted small">{usersPaginationRangeLabel}</span>
          <GridPager
            className="users-pagination__pager"
            onChange={pagingOnChange}
            current={pagingStore.currentPage ?? 1}
            total={pagingStore.totalItems ?? 0}
            defaultPageSize={pagingStore.pageLimit ?? 10}
            showLessItems={true}
            showTitle={false}
            showSizeChanger={true}
            pageSize={pagingStore.pageLimit ?? 10}
          />
        </div>
      </div>

      {/* Bulk Upload Modal */}
      <Modal
        open={showBulkUploadModal}
        onClose={handleCloseBulkUploadModal}
        size="small"
        className="bulk-upload-modal"
      >
        <Modal.Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icon name="upload" />
            <span>{t("app.users.bulkUploadUsers")}</span>
          </div>
        </Modal.Header>
        <Modal.Content>
          <div style={{ padding: '10px 0' }}>
            <p style={{ marginBottom: '20px', color: '#6b7280' }}>
              Upload multiple users at once using an Excel file. Download the template to see the required format.
            </p>

            {/* Download Template Section */}
            <div style={{ marginBottom: '30px', padding: '15px', background: '#f7fafd', borderRadius: '8px', border: '1px solid #e3e8ee' }}>
              <h4 style={{ marginTop: 0, marginBottom: '10px', fontSize: '16px', fontWeight: 600 }}>
                <Icon name="download" style={{ marginRight: '8px' }} />
                Download Template
              </h4>
              <p style={{ marginBottom: '15px', fontSize: '14px', color: '#6b7280' }}>
                Download the Excel template to see the required format and column structure.
              </p>
              <Button
                onClick={handleDownloadTemplate}
                disabled={isDownloadingTemplate}
                loading={isDownloadingTemplate}
                primary
              >
                <Icon name="download" />
                Download Excel Template
              </Button>
            </div>

            {/* Upload File Section */}
            <div style={{ padding: '15px', background: '#f7fafd', borderRadius: '8px', border: '1px solid #e3e8ee' }}>
              <h4 style={{ marginTop: 0, marginBottom: '10px', fontSize: '16px', fontWeight: 600 }}>
                <Icon name="file excel" style={{ marginRight: '8px' }} />
                Upload Excel File
              </h4>
              <p style={{ marginBottom: '15px', fontSize: '14px', color: '#6b7280' }}>
                Select an Excel file (.xlsx or .xls) to upload users.
              </p>
              <input
                type="file"
                id="bulk-upload-file-input"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  onClick={() => document.getElementById('bulk-upload-file-input').click()}
                  primary
                >
                  <Icon name="folder open" />
                  Browse File
                </Button>
                {uploadFile && (
                  <span style={{ color: '#059669', fontSize: '14px', display: 'flex', alignItems: 'start', gap: '5px' }}>
                    <Icon name="check circle" />
                    {uploadFile.name}
                  </span>
                )}
              </div>
            </div>

            {/* Error Display */}
            {uploadErrors && uploadErrors.length > 0 && (
              <div style={{
                marginTop: '20px',
                padding: '15px',
                background: '#fef2f2',
                borderRadius: '8px',
                border: '1px solid #fecaca',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                <h5 style={{ marginTop: 0, marginBottom: '10px', color: '#dc2626', fontSize: '14px', fontWeight: 600 }}>
                  <Icon name="exclamation triangle" style={{ marginRight: '5px' }} />
                  Upload Errors
                </h5>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#991b1b', fontSize: '13px' }}>
                  {uploadErrors.map((error, index) => (
                    <li key={index} style={{ marginBottom: '5px' }}>
                      {typeof error === 'string' ? error : error.message || JSON.stringify(error)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCloseBulkUploadModal}>
            Cancel
          </Button>
          <Button
            primary
            onClick={handleUpload}
            loading={isUploading}
            disabled={!uploadFile || isUploading}
          >
            <Icon name="upload" />
            Upload Users
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={showDeleteModal}
        onClose={handleCancelDelete}
        size="small"
        dimmer="blurring"
        className="delete-confirm-modal"
        style={{
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          background: "#fff",
          border: "none"
        }}
      >
        <Modal.Header
          className="delete-modal-header"
          style={{
            background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)",
            backgroundColor: "transparent",
            color: "white",
            padding: "24px 24px 16px 24px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
            borderRadius: "20px 20px 0 0",
            margin: 0,
            overflow: "hidden",
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px"
          }}
        >
          <div className="d-flex align-items-center">
            <div
              className="me-3"
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="trash alternate" size="large" style={{ color: "white", margin: 0 }} />
            </div>
            <div>
              <h4 className="mb-1 fw-bold" style={{ color: "white", fontSize: "20px", margin: 0 }}>Delete User Permanently</h4>
              <p className="mb-0" style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "13px", margin: 0 }}>
                This will permanently remove the user and all their data
              </p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Content style={{
          padding: "25px",
          background: "#fff",
          borderRadius: 0,
          margin: 0
        }}>
          <div style={{ marginBottom: "20px" }}>
            <p style={{ fontSize: "16px", lineHeight: "1.6", color: "#333", marginBottom: "15px" }}>
              You are about to <strong style={{ color: "#ff6b6b" }}>permanently delete</strong> the user <strong>"{userToDelete?.name}"</strong>.
            </p>
            <div style={{
              background: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "6px",
              padding: "15px",
              marginBottom: "15px"
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <Icon name="warning sign" style={{ color: "#ff9800", marginTop: "2px", flexShrink: 0 }} />
                <div>
                  <strong style={{ color: "#856404", display: "block", marginBottom: "8px" }}>
                    Warning: Permanent Deletion
                  </strong>
                  <p style={{ margin: 0, color: "#856404", fontSize: "14px", lineHeight: "1.5" }}>
                    Once deleted, you cannot recover this user or any of their data. All information will be permanently removed, including:
                  </p>
                  <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px", color: "#856404", fontSize: "14px" }}>
                    <li>User profile and account information</li>
                    <li>Leave records and balances</li>
                    <li>Attendance and time tracking data</li>
                    <li>All associated employee records</li>
                    <li>Any other data linked to this user</li>
                  </ul>
                </div>
              </div>
            </div>
            <p style={{ fontSize: "14px", color: "#666", margin: 0 }}>
              Are you absolutely sure you want to proceed?
            </p>
          </div>
        </Modal.Content>
        <Modal.Actions style={{
          background: "#f8f9fa",
          padding: "15px 25px",
          borderTop: "1px solid #e9ecef",
          borderRadius: "0 0 20px 20px",
          margin: 0
        }}>
          <button
            type="button"
            className="users-delete-modal-cancel-btn"
            onClick={handleCancelDelete}
            style={{
              borderRadius: "6px",
              padding: "10px 20px",
              background: "#61686F",
              color: "#fff",
              border: "none",
              cursor: "pointer",
              fontWeight: 600,
              fontFamily: "inherit"
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="users-delete-permanently-btn"
            onClick={handleConfirmDelete}
            disabled={loading}
            style={{
              borderRadius: "6px",
              padding: "10px 20px",
              background: "#EE6352",
              color: "#fff",
              border: "none",
              cursor: loading ? "not-allowed" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: 600,
              fontFamily: "inherit"
            }}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" style={{ width: "1em", height: "1em" }}></span>
                Deleting...
              </>
            ) : (
              <>
                <span style={{ display: "inline-flex", alignItems: "center", lineHeight: 0 }}>
                  <Icon name="trash alternate" style={{ margin: 0, lineHeight: 1 }} />
                </span>
                Delete Permanently
              </>
            )}
          </button>
        </Modal.Actions>
      </Modal>

      {workplaceRequiredModal}
    </div>
  );
};

export default Users;
